import { Box, Flex, TextV1 } from '@withjoy/joykit';
import React, { useEffect, useRef } from 'react';
import type { CatalogItemTile } from '../Catalog.types';
import { ProductGrid } from '../components';
import { HeroTextWithSkeleton } from '../components/HeroText/HeroText';
import type { CatalogProductFilters } from '../components/RefinementFacet/RefinementFacet.types';
import { setCurationNavigationState } from '../state/Catalog';
import type { CatalogCurationDictionaryEntry } from '../state/Catalog/Catalog.types';
import { useCurationDetailsController } from './CurationDetails.controller';
import { ExploreOthers } from './components/ExploreOthers';

interface CurationDetailsProps {
  showSkeleton?: boolean;
  curationLocator: string;
  filters: CatalogProductFilters;
  selectedCuration: Maybe<Readonly<CatalogCurationDictionaryEntry>>;
  curationResults: ReadonlyArray<CatalogItemTile>;
  hasNextPage: boolean;
  isFetchingMore: boolean;
  onLoadMoreClick: Maybe<() => void>;
}

const CurationDetails: React.FC<CurationDetailsProps> = ({
  filters,
  showSkeleton,
  curationLocator,
  selectedCuration,
  curationResults,
  hasNextPage,
  isFetchingMore,
  onLoadMoreClick,
}) => {
  const { addCatalogItemToRegistry, addCatalogItemLoading, isMobile, isExploreCurationsIntersecting, exploreOthersTriggerPointRef } = useCurationDetailsController();

  const headerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (curationResults.length && selectedCuration) {
      const children = curationResults.flatMap(curationResult => (curationResult.group ? curationResult.group.items.map(x => x.id) : [curationResult.id]));
      const uniqueChildren = Array.from(new Set(children).keys());
      setCurationNavigationState({
        id: selectedCuration.id,
        title: selectedCuration.title,
        children: uniqueChildren,
        publicUrlPath: selectedCuration.publicUrlPath,
      });
    }
  }, [curationResults, selectedCuration, curationLocator]);

  const renderHeader = () => {
    const description =
      (selectedCuration?.curationData?.description && (
        <TextV1
          variant="body1"
          textAlign={isMobile ? 'left' : 'center'}
        >
          {selectedCuration?.curationData?.description}
        </TextV1>
      )) ||
      null;

    const content = (
      <Box>
        <Flex
          alignItems={isMobile ? 'left' : 'center'}
          flexDirection={'column'}
          paddingX={[0, null, 6]}
        >
          {!selectedCuration?.curationData?.header && (
            <HeroTextWithSkeleton
              skeletonRowCount={1}
              shouldRevealSkeleton={!showSkeleton}
              heroText={selectedCuration?.title}
              placeholderProps={{ height: 'fit-content', width: '100%' }}
              textAlign={isMobile ? 'left' : 'center'}
              margin={0}
            />
          )}
          {description}
        </Flex>
        <div ref={headerRef} />
      </Box>
    );

    return (
      <Box
        marginTop={[0, null, null, '56px']}
        marginBottom={['36px', null, '36px', '76px']}
        paddingX={[6, null, 0]}
      >
        {content}
      </Box>
    );
  };

  return (
    <Flex flexDirection="column">
      {renderHeader()}
      <ProductGrid
        filters={filters}
        isMobile={isMobile}
        categoryLocator={selectedCuration?.publicUrlPath}
        categoryType="curation"
        componentSource="productGrid"
        telemetryPageValue="curationDetail"
        showSkeleton={showSkeleton}
        products={curationResults}
        addCatalogItem={addCatalogItemToRegistry}
        addItemLoading={addCatalogItemLoading}
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingMore}
        onLoadMoreClick={onLoadMoreClick}
        disableGridMarginBottom={true}
        disableFilters={true}
        hideFilterToggle={isExploreCurationsIntersecting}
        shouldOpenPdp={true}
      />
      {!showSkeleton && (
        <ExploreOthers
          marginTop={'80px'}
          isMobile={isMobile}
          showSkeleton={showSkeleton}
          selectedCuration={selectedCuration}
          telemetryPageValue="curationDetail"
          ref={exploreOthersTriggerPointRef}
        />
      )}
    </Flex>
  );
};

export { CurationDetails };
